import { CommonModule } from '@angular/common';
import type { OnChanges, SimpleChanges } from '@angular/core';
import { Component, inject, Input } from '@angular/core';

import { DynamicModule } from 'ng-dynamic-component';
import type { Observable } from 'rxjs';
import { BehaviorSubject, combineLatest, map, switchMap } from 'rxjs';

import { ColumnsComponent } from '@valk-nx/storyblok/components/columns/src/lib/columns';
import { StoryblokDirective } from '@valk-nx/storyblok-directives/src/lib/directives/storyblok.directive';
import { StoryblokRootDirective } from '@valk-nx/storyblok-directives/src/lib/directives/storyblok-root.directive';
import { ContentService } from '@valk-nx/storyblok-services/src/lib/services/content.service';
import { StoryblokService } from '@valk-nx/storyblok-services/src/lib/services/storyblok.service';
import type { ISbStoryBlok } from '@valk-nx/storyblok-types/src/lib/types/storyblok.interface';

@Component({
	selector: 'sb-static-section',
	templateUrl: './static-section.html',
	imports: [
		CommonModule,
		ColumnsComponent,
		DynamicModule,
		StoryblokDirective,
	],
})
export class StoryblokStaticSectionComponent
	extends StoryblokRootDirective
	implements OnChanges
{
	private readonly contentService = inject(ContentService);
	private readonly sbService = inject(StoryblokService);

	@Input({ required: true }) content!: string;

	staticData$: Observable<ISbStoryBlok[]>;
	content$ = new BehaviorSubject<string>('');

	constructor() {
		super();

		const selectedSlug$ = this.sbService.translatedSlugs$.pipe(
			map((translatedSlugs) => {
				return (
					translatedSlugs.find((x) => x.selected) ??
					translatedSlugs[0]
				);
			}),
		);

		const contentWithSlug$ = combineLatest([
			selectedSlug$,
			this.content$,
		]).pipe(map(([slug, content]) => ({ slug, content })));

		this.staticData$ = contentWithSlug$.pipe(
			switchMap(({ slug, content }) => {
				return this.contentService.getLegal(
					content,
					slug.lang.toLowerCase(),
				);
			}),
			map((response) => {
				return [
					{
						component: 'markdown',
						content: response?.data,
					} as unknown as ISbStoryBlok,
				];
			}),
		);
	}

	ngOnChanges(sc: SimpleChanges) {
		this.content$.next(sc['content'].currentValue);
	}
}
